import { Injectable } from '@angular/core';

interface EncryptResult {
  ciphertext: string;
  iv: string;
  tag: string;
}

@Injectable({ providedIn: 'root' })
export class ControlSafeCryptoService {
  private readonly tagLength = 16;

  async deriveKey(password: string, salt: string): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      { name: 'PBKDF2' },
      false,
      ['deriveKey']
    );
    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: encoder.encode(salt),
        iterations: 100000,
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  async encrypt(plainText: string, key: CryptoKey): Promise<EncryptResult> {
    const encoder = new TextEncoder();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      encoder.encode(plainText)
    );
    const encryptedBytes = new Uint8Array(encrypted);
    const ciphertext = encryptedBytes.slice(0, encryptedBytes.length - this.tagLength);
    const tag = encryptedBytes.slice(encryptedBytes.length - this.tagLength);
    return {
      ciphertext: this.toBase64(ciphertext),
      iv: this.toBase64(iv),
      tag: this.toBase64(tag)
    };
  }

  async decrypt(ciphertext: string, iv: string, tag: string, key: CryptoKey): Promise<string> {
    const ciphertextBytes = this.fromBase64(ciphertext);
    const tagBytes = this.fromBase64(tag);
    const ivBytes = this.fromBase64(iv);
    const combined = new Uint8Array(ciphertextBytes.length + tagBytes.length);
    combined.set(ciphertextBytes);
    combined.set(tagBytes, ciphertextBytes.length);
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivBytes },
      key,
      combined
    );
    const decoder = new TextDecoder();
    return decoder.decode(decrypted);
  }

  private toBase64(value: Uint8Array): string {
    let binary = '';
    value.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return btoa(binary);
  }

  private fromBase64(value: string): Uint8Array {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}
